import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import {userURL} from 'dao/UserDAO';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import Store from 'lib/Store';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import * as OrganizationUtil from 'orgs/OrganizationUtil';
import EntityStore from 'stores/EntityStore';

import {
  ALL_SCREENS,
  ORGANIZATION_PAGE_TAB_TO_URL_PARAM,
  USER_PAGE_TAB_TO_URL_PARAM,
  PROJECTS_TAB,
  SETTINGS_TAB,
} from './OrganizationConstants';
import OrganizationDAO from './OrganizationDAO';

const {organizationURL} = OrganizationDAO;

import {teamURL} from './TeamDAO';

class PartyPageStore extends Store {
  resetState() {
    // Store the currentOrganization which is the state of the organization on the organization page.
    // This may fall out of sync with the organization server (e.g. on the settings tab before a save has
    // been made). When the organization has been saved, the current organization is updated with the
    // server's response.

    FullScreenStore.resetState();

    // Page State
    this._resetPageState();

    // Organization State
    this.currentOrganization = null;
    this.organizationMemberInvites = null;
    this.organizationMemberRequests = null;
    this.organizationProjects = null;
    this.organizationTeams = null;

    // Team State
    this.currentTeam = null;
    this.teamProjects = null;
  }

  _syncURL(url, replace = false) {
    navigateToURL(url, {skipTrigger: true, replace});
  }

  _resetPageState() {
    // API identifier of the entity being viewed. This should be `teamId` for team pages,
    // `organizationId` for organization pages, and `userId` for user pages.
    // Note: Teams are not technically "entities" since they do not extend the "Entity" model, but we
    // consider them entities as they share similar properties to users and organizations.
    this.currentEntityId = null;
    this.activeTab = null;
    this.isLoading = false;
    // Both organizations and teams show member info, but these could be separated to improve caching.
    this.userIdToMemberInfo = null;
  }

  isScreenSupported(screen) {
    return ALL_SCREENS.includes(screen);
  }

  getCurrentEntity() {
    if (!this.currentEntityId) {
      return null;
    }
    if (OrganizationUtil.isTeam(this.currentEntityId)) {
      return this.currentTeam;
    }
    return this.currentOrganization;
  }

  _getCurrentURL() {
    assert(this.currentEntityId, 'Current entity id cannot be undefined.');
    let urlSection;
    let url;
    if (OrganizationUtil.isTeam(this.currentEntityId)) {
      urlSection = this.activeTab ? ORGANIZATION_PAGE_TAB_TO_URL_PARAM[this.activeTab] : null;
      url = teamURL(this.currentTeam.id);
    } else if (this.currentOrganization != null) {
      urlSection = this.activeTab ? ORGANIZATION_PAGE_TAB_TO_URL_PARAM[this.activeTab] : null;
      url = organizationURL(this.currentOrganization.url_identifier);
    } else {
      urlSection = this.activeTab ? USER_PAGE_TAB_TO_URL_PARAM[this.activeTab] : null;
      url = userURL(this.currentEntityId);
    }
    return `${url}/${urlSection}`;
  }

  getCurrentScreen() {
    const currentScreen = FullScreenStore.getCurrentScreen();
    return this.isScreenSupported(currentScreen) ? currentScreen : null;
  }

  getCurrentOrganization() {
    return this.currentOrganization;
  }

  getIsLoading() {
    return this.isLoading;
  }

  getOrganizationMemberInvites() {
    return this.organizationMemberInvites;
  }

  getOrganizationMemberRequests() {
    return this.organizationMemberRequests;
  }

  getOrganizationProjects() {
    return this.organizationProjects;
  }

  getOrganizationTeams() {
    return this.organizationTeams;
  }

  getTeamProjects() {
    return this.teamProjects;
  }

  getUserIdToMemberInfo() {
    return this.userIdToMemberInfo;
  }

  getCurrentTeam() {
    return this.currentTeam;
  }

  getUserPageUserId() {
    return this.getCurrentOrganization() == null && this.getCurrentTeam() == null
      ? this.currentEntityId
      : null;
  }

  getActiveTab() {
    return this.activeTab;
  }

  _updateActiveTab(tab, replaceUrl = false) {
    this.activeTab = tab;
    const url = this._getCurrentURL();
    this._syncURL(url, replaceUrl);
  }

  /*
   * Handles changing the Organization and Team page being viewed. This will handle evicting stale
   * information from the store. Switching from two different organizations will reset the entire store
   * state, whereas switching from a team to another team in the same organization will not evict all of
   * the organization state.
   * This also updates `currentEntityId`, which tracks the teamId or organizationId currently being viewed.
   */
  _setCurrentEntity(entity) {
    this._resetPageState();
    // If currently viewing the team page and the team's organization is not the same as currentOrganization,
    // reset the store state.
    if (OrganizationUtil.isTeam(entity.id)) {
      if (this.currentOrganization && this.currentOrganization.id !== entity.organizationId) {
        this.resetState();
      }
      this.currentEntityId = entity.id;
      this.currentTeam = entity;
      return;
    }
    // If a previous organization page was open, reset the store state.
    if (this.currentOrganization && this.currentOrganization.id !== entity.id) {
      this.resetState();
    }
    this.currentEntityId = entity.id;
    this.currentOrganization = entity;
  }

  payloadHandler(action) {
    switch (action.actionType) {
      case EventConstants.OPEN_ORGANIZATION_PAGE_COMPLETED:
        this._setCurrentEntity(action.organization);
        BenchlingUtil.setWindowTitle(this.currentOrganization.name);
        this._updateActiveTab(action.activeTab || PROJECTS_TAB, true);
        this.emitChange();
        break;

      case EventConstants.OPEN_TEAM_PAGE_COMPLETED:
        this._setCurrentEntity(action.team);
        this.currentOrganization = action.organization;
        BenchlingUtil.setWindowTitle(this.currentTeam.name);
        this._updateActiveTab(action.activeTab || PROJECTS_TAB, true);
        this.emitChange();
        break;

      case EventConstants.OPEN_USER_PAGE_COMPLETED:
        this._resetPageState();
        this.currentTeam = null;
        this.currentOrganization = null;
        this.currentEntityId = action.userId;
        this._updateActiveTab(action.activeTab || SETTINGS_TAB, true);
        this.emitChange();
        break;

      case EventConstants.UPDATE_ACTIVE_TAB:
        this._updateActiveTab(action.activeTab);
        this.emitChange();
        break;

      case EventConstants.ORG_UPDATE_COMPLETED:
        if (this.currentOrganization != null && action.organization.id === this.currentOrganization.id) {
          this.currentOrganization = action.organization;
          this.isLoading = false;
          this.emitChange();
        }
        break;

      case EventConstants.TEAM_UPDATE_COMPLETED:
        if (this.currentTeam != null && action.team.id === this.currentTeam.id) {
          this.currentTeam = action.team;
          this.isLoading = false;
          this.emitChange();
        }
        break;

      case EventConstants.OPEN_ORGANIZATION_PROJECTS_TAB:
        this.organizationProjects = action.projects;
        this.emitChange();
        break;

      case EventConstants.OPEN_TEAM_PROJECTS_TAB:
        this.teamProjects = action.projects;
        this.emitChange();
        break;

      case EventConstants.OPEN_ORGANIZATION_TEAMS_TAB:
        this.organizationTeams = action.organizationTeams;
        this.emitChange();
        break;

      case EventConstants.OPEN_ORGANIZATION_PAGE:
      case EventConstants.OPEN_TEAM_PAGE:
      case EventConstants.ORG_UPDATE:
      case EventConstants.ORG_CREATE:
      case EventConstants.ORGANIZATION_SHARE_PROJECTS:
      case EventConstants.TEAM_UPDATE:
        if (this.getCurrentScreen() != null) {
          this.isLoading = true;
          this.emitChange();
        }
        break;

      case EventConstants.OPEN_CREATE_TEAM_PAGE_FAILED:
      case EventConstants.OPEN_ORGANIZATION_PAGE_FAILED:
      case EventConstants.OPEN_TEAM_PAGE_FAILED:
      case EventConstants.ORG_CREATE_FAILED:
      case EventConstants.ORG_UPDATE_FAILED:
      case EventConstants.ORGANIZATION_SHARE_PROJECTS_FAILED:
      case EventConstants.TEAM_UPDATE_FAILED:
        if (this.getCurrentScreen() != null) {
          this.isLoading = false;
          this.emitChange();
        }
        break;

      case EventConstants.OPEN_MEMBERS_TAB_COMPLETED:
        AppDispatcher.waitFor([EntityStore.dispatchIndex]);
        this.userIdToMemberInfo = action.userIdToMemberInfo;
        this.organizationMemberInvites = action.organizationMemberInvites;
        this.organizationMemberRequests = action.organizationMemberRequests;
        this.emitChange();
        break;

      case EventConstants.MEMBER_PERMISSION_UPDATE_COMPLETED:
        if (this.userIdToMemberInfo != null) {
          AppDispatcher.waitFor([EntityStore.dispatchIndex]);
          this.userIdToMemberInfo = reactUpdate(this.userIdToMemberInfo, {
            [action.userId]: {
              $merge: {
                loading: false,
              },
            },
          });
          this.emitChange();
        }
        break;

      case EventConstants.MEMBER_PERMISSION_UPDATE:
      case EventConstants.MEMBER_REMOVAL:
        if (this.getCurrentScreen() != null) {
          this.userIdToMemberInfo = reactUpdate(this.userIdToMemberInfo, {
            [action.userId]: {
              $merge: {
                loading: true,
              },
            },
          });
          this.emitChange();
        }
        break;

      case EventConstants.MEMBER_PERMISSION_UPDATE_FAILED:
      case EventConstants.MEMBER_REMOVAL_FAILED:
        if (this.getCurrentScreen() != null) {
          this.userIdToMemberInfo = reactUpdate(this.userIdToMemberInfo, {
            [action.userId]: {
              $merge: {
                loading: false,
              },
            },
          });
          this.emitChange();
        }
        break;

      case EventConstants.MEMBER_REMOVAL_COMPLETED:
        if (this.userIdToMemberInfo != null) {
          // eslint-disable-next-line no-restricted-syntax
          this.userIdToMemberInfo = _.omit(this.userIdToMemberInfo, action.userId);
          this.emitChange();
        }
        break;

      case EventConstants.OPEN_CREATE_ORGANIZATION_PAGE:
        // CreateOrganizationPage doesn't need anything in the store. We set `currentOrganization`
        // to pre-fill the create form with a name if provided from SearchOrganizationModal.
        this.resetState();
        this.currentOrganization = action.organization;
        this._syncURL('/organizations/create');
        BenchlingUtil.setWindowTitle('Create Organization');
        this.emitChange();
        break;

      case EventConstants.OPEN_CREATE_TEAM_PAGE:
        this.resetState();
        this.currentOrganization = action.organization;
        BenchlingUtil.setWindowTitle('Create Team');
        this.emitChange();
        break;

      case EventConstants.ORG_CREATE_COMPLETED:
        this._setCurrentEntity(action.organization);
        this.isLoading = false;
        this.emitChange();
        break;

      case EventConstants.ORGANIZATION_SHARE_PROJECTS_COMPLETED:
        this.isLoading = false;
        this._updateActiveTab(action.activeTab || PROJECTS_TAB);
        this.emitChange();
        break;

      case EventConstants.ORGANIZATION_CANCEL_INVITE:
        this.organizationMemberInvites = this.organizationMemberInvites.filter(
          ({email}) => email !== action.email
        );
        this.organizationMemberRequests = this.organizationMemberRequests.filter(
          ({user}) => user.email !== action.email
        );
        this.emitChange();
        break;

      default:
        break;
    }
    return true;
  }
}

export default new PartyPageStore(AppDispatcher);
